import { McpServerModel, McpToolSummary } from './McpServerModel';

/**
 * Rough token-cost estimation for MCP tool definitions, used to warn users about context/token bloat
 * and to power per-tool selection. Deliberately a cheap ~chars/4 heuristic (the same ratio the on-device
 * workflow model's {@link import('@desktop/services/inference/LlamaServerChatProvider')} uses) so it works
 * with no dependency on the model server running. It approximates what a tool costs when its definition
 * (name + description + JSON input schema) is injected into the agent's context.
 */

/** Average characters per token for the ~chars/4 heuristic. */
const AVG_CHARS_PER_TOKEN = 4;

/** A tool shape sufficient to estimate its definition size (live tool or cached summary). */
export interface EstimatableTool {
    name: string;
    description?: string;
    /** JSON schema / Zod schema / params object for the tool's input, if available. */
    schema?: unknown;
}

/** Safely stringify a value for sizing; returns '' on circular refs or unserializable input. */
function safeStringify(value: unknown): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value) ?? '';
    } catch {
        return '';
    }
}

/**
 * Estimate the token cost of a single tool's full definition (name + description + input schema).
 * Heuristic: `ceil(totalChars / 4)`.
 */
export function estimateToolTokens(tool: EstimatableTool): number {
    const chars = tool.name.length + (tool.description?.length ?? 0) + safeStringify(tool.schema).length;
    return Math.ceil(chars / AVG_CHARS_PER_TOKEN);
}

/** True when a tool (by bare name) is enabled on a server — i.e. not in `disabledTools`. */
export function isToolEnabled(server: Pick<McpServerModel, 'disabledTools'>, toolName: string): boolean {
    return !server.disabledTools?.includes(toolName);
}

/**
 * Sum of the cached tools' token estimates for a server. With `onlyEnabled`, tools in `disabledTools`
 * are excluded (i.e. the cost the agent will actually incur). Tools missing a `tokenEstimate` count as 0.
 */
export function serverToolTokenTotal(
    server: Pick<McpServerModel, 'cachedTools' | 'disabledTools'>,
    opts: { onlyEnabled?: boolean } = {}
): number {
    return (server.cachedTools ?? []).reduce((sum, tool: McpToolSummary) => {
        if (opts.onlyEnabled && !isToolEnabled(server, tool.name)) return sum;
        return sum + (tool.tokenEstimate ?? 0);
    }, 0);
}

/** Count of enabled tools (not in `disabledTools`) among a server's cached tools. */
export function enabledToolCount(server: Pick<McpServerModel, 'cachedTools' | 'disabledTools'>): number {
    return (server.cachedTools ?? []).filter((tool) => isToolEnabled(server, tool.name)).length;
}
